import { withStoreRequiredForEditor } from '@doltech/ui/lib/figma/BlogEditor/core/withStoreRequiredForEditor';
import { useDoExerciseProgressContext } from '@doltech/ui/lib/figma/BlogEditor/shared-components/Exercise/DoExerciseProgressContext';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { compose } from '@doltech/utils/lib/compose';
import {
  SectionDescriptionSectionTypeEnum,
  SpeakingSampleTypeEnum,
} from '@doltech/utils/lib/constants';
import { getPageSampleTitleEnumTextByKey } from '@doltech/utils/lib/enum/PageSampleTitleEnum';
import { getTocTitleById } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';
import { VocabList } from '../../../../view-vocab/components/VocabList';
import { AuthorPreview } from '../../../components/Preview/AuthorPreview';
import { IeltsSampleHeader } from '../../../components/Preview/IeltsSampleHeader';
import { LeftSidebarPreviewSample } from '../../../components/Preview/LeftSidebarPreviewSample';
import { SampleInteractionBottomNav } from '../../../components/SampleInteractionBottomNav';
import {
  getSampleSectionHides,
  IeltsSampleHideableSectionEnum,
  PreviewMain,
} from '../../../sample.utils';
import { TitleH2Block } from '../../../Writing/Steps/SamplePreview';
import {
  speakingPart1TableOfContent,
  SpeakingPart1TocId,
} from '../../CreateSpeakingTaskSample.step';
import dynamic from 'next/dynamic';
import { SamplePromotionSidebar } from '../../../components/Preview/SamplePromotionSidebar';

const ExerciseEditorPreview = dynamic<any>(() =>
  import('../../../Writing/Steps/SampleExercisesPreview').then((mod) => mod.ExerciseEditorPreview)
);

const ConclusionPreview = dynamic<any>(() =>
  import('../../../components/Preview/ConclusionPreview').then((mod) => mod.ConclusionPreview)
);

const FlowVocabIdeaPreview = dynamic<any>(
  () =>
    import('../../../components/Preview/FlowVocabIdeaPreview').then(
      (mod) => mod.FlowVocabIdeaPreview
    ),
  {
    ssr: false,
  }
);
const QuestionListPreview = dynamic<any>(() =>
  import('../../../components/Preview/QuestionListPreview').then((mod) => mod.QuestionListPreview)
);
const SectionDescriptionPreview = dynamic<any>(() =>
  import('../../../components/Preview/SectionDescriptionPreview').then(
    (mod) => mod.SectionDescriptionPreview
  )
);

const Main = styled(PreviewMain)``;
const SpeakingPart1PreviewBase = (props: any) => {
  const { seoData = { id: 'unknown' }, detailData, staticPageLink } = props;

  const sectionHides = getSampleSectionHides(detailData);
  const linkSeeMore =
    detailData.type === SpeakingSampleTypeEnum.SPEAKING_PART_1
      ? staticPageLink?.LANDING_SPEAKING_SAMPLE_P1?.url
      : staticPageLink?.LANDING_SPEAKING_SAMPLE_P3?.url;
  const doExerciseContext = useDoExerciseProgressContext();

  React.useEffect(() => {
    if (seoData && !doExerciseContext.pageId) {
      doExerciseContext.setPageId(seoData.id);
    }
  }, [doExerciseContext, seoData]);

  return (
    <Main>
      <SamplePromotionSidebar />
      <div className={cl('section-container')}>
        <div className="section-block">
          <IeltsSampleHeader
            routes={[
              {
                path: '/',
                breadcrumbName: 'Trang chủ',
              },
              {
                path: staticPageLink?.LANDING_IELTS_KNOWLEDGE?.url || '/',
                breadcrumbName: 'Kiến thức IELTS',
              },
              {
                path: staticPageLink?.LANDING_SPEAKING_SAMPLE?.url || '/',
                breadcrumbName: 'IELTS Speaking Sample',
              },
              {
                path: linkSeeMore || '/',
                breadcrumbName: getPageSampleTitleEnumTextByKey(detailData.type),
              },
            ]}
            title={detailData.title}
            description={detailData.description}
            lastModifiedAt={detailData?.lastModifiedAt}
            length={detailData?.length}
            seoData={seoData}
          />
        </div>

        {detailData.isTocShown && (
          <LeftSidebarPreviewSample
            items={speakingPart1TableOfContent.filter((item) => {
              if (
                sectionHides[IeltsSampleHideableSectionEnum.EXERCISE] &&
                item.id === SpeakingPart1TocId.EXERCISES
              ) {
                return false;
              }
              return !(
                sectionHides[IeltsSampleHideableSectionEnum.CONCLUSION] &&
                item.id === SpeakingPart1TocId.LOI_KET
              );
            })}
            tocTitle={detailData.title}
          />
        )}

        <div id={SpeakingPart1TocId.DANH_SACH_CAU_HOI} className="section-block">
          <TitleH2Block>
            {getTocTitleById(SpeakingPart1TocId.DANH_SACH_CAU_HOI, speakingPart1TableOfContent)}
          </TitleH2Block>

          <SectionDescriptionPreview
            detailData={detailData}
            sectionType={SectionDescriptionSectionTypeEnum.QUESTIONS}
          />

          <div className="left-sidebar-stick right-sidebar-stick">
            <QuestionListPreview detailData={detailData} />
          </div>
        </div>

        <Separator variant="three-dot-horizontal" />
        <div id={SpeakingPart1TocId.SAMPLE_TUNG_CAU} className="section-block">
          <FlowVocabIdeaPreview
            title={getTocTitleById(SpeakingPart1TocId.SAMPLE_TUNG_CAU, speakingPart1TableOfContent)}
            withTitleNode
            detailData={detailData}
            audioMode="MULTIPLE"
            renderDescription={() => (
              <SectionDescriptionPreview
                detailData={detailData}
                sectionType={SectionDescriptionSectionTypeEnum.SAMPLES}
              />
            )}
          />
        </div>

        <Separator variant="three-dot-horizontal" />
        <div id={SpeakingPart1TocId.VOCABULARY} className="section-block">
          <TitleH2Block>
            {getTocTitleById(SpeakingPart1TocId.VOCABULARY, speakingPart1TableOfContent)}
          </TitleH2Block>
          <SectionDescriptionPreview
            detailData={detailData}
            sectionType={SectionDescriptionSectionTypeEnum.VOCABS}
          />
          <VocabList vocabList={detailData?.vocab?.vocabs || []} />
        </div>

        {!sectionHides[IeltsSampleHideableSectionEnum.EXERCISE] && (
          <>
            <Separator variant="three-dot-horizontal" />
            <div id={SpeakingPart1TocId.EXERCISES} className="section-block">
              <TitleH2Block>
                {getTocTitleById(SpeakingPart1TocId.EXERCISES, speakingPart1TableOfContent)}
              </TitleH2Block>
              <ExerciseEditorPreview detailData={detailData} />
            </div>
          </>
        )}

        {!sectionHides[IeltsSampleHideableSectionEnum.CONCLUSION] && (
          <>
            <Separator variant="three-dot-horizontal" />

            <div id={SpeakingPart1TocId.LOI_KET} className="section-block">
              <TitleH2Block>
                {getTocTitleById(SpeakingPart1TocId.LOI_KET, speakingPart1TableOfContent)}
              </TitleH2Block>
              <ConclusionPreview detailData={detailData} />
            </div>
          </>
        )}

        <Separator variant="three-dot-horizontal" />
        <div id="footer-author" className="section-block">
          <AuthorPreview
            seoData={seoData}
            lastModifiedAt={detailData.lastModifiedAt}
            length={detailData.length}
          />
        </div>
      </div>

      <SampleInteractionBottomNav
        seoData={seoData}
        tocItems={speakingPart1TableOfContent}
        tocTitle={detailData.title}
      />
    </Main>
  );
};

export const SpeakingPart1PreviewUI = compose(
  withStoreRequiredForEditor({
    haveDoTestExercise: true,
    initFileUploadImmediately: true,
  }),
  observer
)(SpeakingPart1PreviewBase);
